/**
 * «¿No puedes entrar?» — la salida que faltaba en el acceso.
 *
 * Aquí NO hay «restablecer contraseña», y no es un descuido: la contraseña con la que se entra a
 * Orvian es la del BUZÓN del proveedor (Hostinger por defecto). Orvian no la elige, no la guarda en
 * claro y no puede cambiarla; solo la comprueba contra el IMAP en cada acceso. Un formulario de
 * «te enviamos un enlace para crear una contraseña nueva» sería mentira: la nueva contraseña hay
 * que ponerla donde vive el buzón.
 *
 * Lo que sí puede hacer esta página es lo que antes no hacía nadie: decirle a la persona qué le
 * está pasando y a quién acudir, en vez de dejarla delante de un campo que no acepta su clave.
 */
import React, { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import { ArrowLeft, KeyRound, Mail, ShieldCheck, Building2, Smartphone, Send } from 'lucide-react';
import { api } from '../lib/api';

const CAUSAS = [
  {
    Icon: KeyRound,
    titulo: 'Cambiaste la contraseña del buzón hace poco',
    texto: 'Entra con la nueva. Orvian usa exactamente la misma contraseña que tu correo: si la '
      + 'cambiaste en el panel de tu proveedor, la de antes ya no sirve aquí tampoco.',
  },
  {
    Icon: Mail,
    titulo: 'No recuerdas la contraseña de tu buzón',
    texto: 'Orvian no puede restablecerla: la contraseña vive en tu proveedor de correo, no aquí. '
      + 'Quien administra el correo de tu empresa puede ponerte una nueva desde el panel del '
      + 'proveedor; en cuanto la tengas, entra con ella.',
  },
  {
    Icon: ShieldCheck,
    titulo: 'Entras con passkey pero el correo no carga',
    texto: 'La passkey te identifica, pero no refresca la contraseña guardada para hablar con tu '
      + 'buzón. Si cambiaste esa contraseña en el proveedor, entra UNA vez con correo y '
      + 'contraseña: a partir de ahí la passkey vuelve a funcionar con todo.',
  },
  {
    Icon: Send,
    titulo: 'Entras, pero no se envía ni llega correo nuevo',
    texto: 'Tu proveedor dejó de aceptar la contraseña que Orvian tenía guardada (casi siempre porque '
      + 'se cambió en el panel del proveedor). Arriba verás un aviso rojo: pulsa «Volver a entrar» y '
      + 'escribe la contraseña actual de tu buzón. En cuanto entres, todo se reanuda solo.',
  },
  {
    Icon: Smartphone,
    titulo: 'Perdiste el teléfono o el código de dos pasos no funciona',
    texto: 'Si el código no entra, revisa que la hora de tu teléfono esté en automático: un reloj '
      + 'desfasado da códigos que no valen. Si ya no tienes la app de códigos, pide a quien administra '
      + 'el correo que te la restablezca (Admin → Usuarios → Restablecer 2FA); podrás volver a activarla al entrar.',
  },
  {
    Icon: Building2,
    titulo: 'El dominio de tu correo no está dado de alta',
    texto: 'Orvian solo deja entrar a los dominios registrados por un administrador. Si tu empresa '
      + 'acaba de empezar a usar la plataforma, pide que añadan tu dominio antes de intentarlo.',
  },
];

export default function AyudaAcceso() {
  const [soporte, setSoporte] = useState(null);   // null = consultando; '' = sin respuesta

  useEffect(() => {
    // Best-effort: si el backend no lo dice, la página sigue valiendo sin el botón de escribir.
    api.get('/auth/soporte')
      .then((r) => setSoporte(r.data?.email || ''))
      .catch(() => setSoporte(''));
  }, []);

  return (
    <div className="flex min-h-screen flex-col" style={{ background: 'var(--bg)' }}>
      <main className="flex flex-1 items-center justify-center px-6 py-10">
        <div className="w-full" style={{ maxWidth: 520 }}>
          <Link
            to="/login"
            className="mb-8 inline-flex items-center gap-1.5"
            style={{ fontSize: 13, color: 'var(--text-2)', textDecoration: 'none' }}
          >
            <ArrowLeft className="h-3.5 w-3.5" /> Volver al acceso
          </Link>

          <h1 style={{ fontSize: 26, fontWeight: 600, color: 'var(--text)', marginBottom: 10 }}>
            ¿No puedes entrar?
          </h1>
          <p style={{ fontSize: 14, lineHeight: 1.6, color: 'var(--text-2)', marginBottom: 28 }}>
            Tu contraseña de Orvian es la misma que la de tu buzón de correo. La guardamos cifrada solo
            para conectar con tu buzón, pero no podemos cambiarla: la fija tu proveedor. Estas son las razones más comunes
            por las que un acceso falla.
          </p>

          <ul className="space-y-5">
            {CAUSAS.map(({ Icon, titulo, texto }) => (
              <li key={titulo} className="flex gap-3">
                <span
                  className="flex shrink-0 items-center justify-center"
                  style={{
                    width: 34, height: 34, borderRadius: 10,
                    background: 'var(--panel-2)', color: 'var(--text-2)',
                  }}
                >
                  <Icon className="h-4 w-4" />
                </span>
                <div>
                  <div style={{ fontSize: 14, fontWeight: 600, color: 'var(--text)', marginBottom: 3 }}>
                    {titulo}
                  </div>
                  <p style={{ fontSize: 13, lineHeight: 1.6, color: 'var(--text-2)' }}>{texto}</p>
                </div>
              </li>
            ))}
          </ul>

          {soporte && (
            <a
              href={`mailto:${soporte}?subject=${encodeURIComponent('No puedo entrar a Orvian')}`}
              className="mt-8 flex w-full items-center justify-center font-semibold"
              data-testid="ayuda-escribir-soporte"
              style={{
                borderRadius: 9999, background: 'var(--accent)', color: '#fff',
                padding: '13px 20px', fontSize: 15, textDecoration: 'none',
              }}
            >
              Escribir a quien administra el correo
            </a>
          )}
          {/* Si /auth/soporte no responde (servidor caído), la página no se queda sin salida (A8). */}
          {soporte === '' && (
            <p className="mt-8" data-testid="ayuda-soporte-alterno" style={{ fontSize: 14, lineHeight: 1.6, color: 'var(--text)' }}>
              Si nada de esto lo resuelve, pide ayuda a quien administra el correo de tu empresa
              (por teléfono o en persona, si tampoco puedes escribir correos).
            </p>
          )}

          <p className="mt-6" style={{ fontSize: 12, lineHeight: 1.6, color: 'var(--text-3)' }}>
            Si escribes pidiendo ayuda, di con qué correo intentas entrar y qué mensaje te sale.
            Con eso se resuelve casi siempre a la primera.
          </p>
        </div>
      </main>
    </div>
  );
}
